import firebase from "firebase/compat/app";
import { auth, db, functions, messaging } from "../firebase";

// Token de push de este dispositivo (fcmToken en el navegador, fcmTokenAndroid
// en la app) y en qué campo de users/{uid} quedó guardado. Al cerrar sesión se
// borra de esa cuenta para que sus notificaciones no le sigan llegando a este
// dispositivo cuando entra otra cuenta (ver usePushNotifications.js y la
// función dedupePushTokens, que lo saca del resto de las cuentas).
const KEY = "pushToken";

export const rememberPushToken = (field, token) => {
  try {
    localStorage.setItem(KEY, JSON.stringify({ field, token }));
  } catch (_) {
    // localStorage puede no estar disponible; no es crítico
  }
};

// Guarda el token en la cuenta que tiene la sesión y lo saca de cualquier otra.
// Se repite en cada apertura porque Firestore no dispara dedupePushTokens si el
// valor no cambió. Si la respuesta nativa llega tarde y la sesión ya es otra (o
// se cerró), no se escribe: el token volvería a la cuenta anterior.
export const saveDevicePushToken = async (uid, field, token) => {
  if (auth.currentUser?.uid !== uid) return;
  rememberPushToken(field, token);
  try {
    await db.doc(`users/${uid}`).set({ [field]: token }, { merge: true });
    await functions.httpsCallable("claimPushToken")({ field, token });
  } catch (error) {
    console.error("Error guardando el token de push:", error);
  }
};

const removeTokenFromAccount = async (uid, { field, token }) => {
  const ref = db.doc(`users/${uid}`);
  const snap = await ref.get();
  // Solo si sigue siendo el de este dispositivo: otro puede haberlo pisado.
  if (snap.exists && snap.data()[field] === token) {
    await ref.update({ [field]: firebase.firestore.FieldValue.delete() });
  }
};

// En el navegador el token se puede invalidar: aunque quede guardado en alguna
// cuenta, ya no llega y el servidor lo borra en el próximo envío. El timeout es
// para que sin conexión no trabe el cierre de sesión.
const invalidateBrowserToken = async () => {
  try {
    await Promise.race([
      messaging.deleteToken(),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]);
  } catch (error) {
    console.error("No se pudo invalidar el token del navegador:", error);
  }
};

export const forgetPushToken = async (uid) => {
  let saved = null;
  try {
    saved = JSON.parse(localStorage.getItem(KEY) || "null");
  } catch (_) {
    saved = null;
  }
  if (!uid || !saved?.field || !saved?.token) return;
  try {
    await removeTokenFromAccount(uid, saved);
  } catch (_) {
    // Un reintento: después del signOut ya no hay permiso para borrarlo.
    try {
      await removeTokenFromAccount(uid, saved);
    } catch (error) {
      console.error("No se pudo borrar el token de push al cerrar sesión:", error);
    }
  }
  if (saved.field === "fcmToken") await invalidateBrowserToken();
};
